import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";
import { useCallback } from "react";

interface TreeNode {
  id: string;
  label: "Persona" | "Outcome" | "Scenario" | "Step" | "Action" | "Api";
  data: any;
  children?: TreeNode[];
}

interface FunctionalGraphItem {
  id: string;
  [key: string]: any;
}

// Fetch all items for a given label (uses same format as use-get-functional-graph-service)
const fetchAllItems = async (
  projectUuid: string,
  label: string,
  headers: Record<string, string>
): Promise<FunctionalGraphItem[]> => {
  // Use same URL format as the existing service, with high limit
  let url = `${config.isometricApiUrl}/functional-graph/${projectUuid}/${label}?page=1&limit=500`;

  // Add sorting for non-Api labels (same as existing service)
  if (label !== "Api") {
    url += `&sortName=${label.toLowerCase()}&sortOrder=asc`;
  }

  const response = await apiFetch(url, { headers });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Failed to fetch ${label}: ${errorText}`);
  }

  const result = await response.json();
  return result.data || [];
};

// Build tree structure from flat data
const buildTree = (
  personas: FunctionalGraphItem[],
  outcomes: FunctionalGraphItem[],
  scenarios: FunctionalGraphItem[],
  steps: FunctionalGraphItem[],
  actions: FunctionalGraphItem[],
  apis: FunctionalGraphItem[]
): TreeNode[] => {
  // Create lookup maps for efficient child finding
  const outcomesByPersona = new Map<string, FunctionalGraphItem[]>();
  const scenariosByOutcome = new Map<string, FunctionalGraphItem[]>();
  const stepsByScenario = new Map<string, FunctionalGraphItem[]>();
  const actionsByStep = new Map<string, FunctionalGraphItem[]>();
  const apisByAction = new Map<string, FunctionalGraphItem[]>();

  outcomes.forEach((o) => {
    if (!o.personaId) return;
    const list = outcomesByPersona.get(o.personaId) || [];
    list.push(o);
    outcomesByPersona.set(o.personaId, list);
  });

  scenarios.forEach((s) => {
    if (!s.outcomeId) return;
    const list = scenariosByOutcome.get(s.outcomeId) || [];
    list.push(s);
    scenariosByOutcome.set(s.outcomeId, list);
  });

  steps.forEach((s) => {
    if (!s.scenarioId) return;
    const list = stepsByScenario.get(s.scenarioId) || [];
    list.push(s);
    stepsByScenario.set(s.scenarioId, list);
  });

  actions.forEach((a) => {
    if (!a.stepId) return;
    const list = actionsByStep.get(a.stepId) || [];
    list.push(a);
    actionsByStep.set(a.stepId, list);
  });

  apis.forEach((a) => {
    if (!a.actionId) return;
    const list = apisByAction.get(a.actionId) || [];
    list.push(a);
    apisByAction.set(a.actionId, list);
  });

  // Build tree recursively
  const buildApiNodes = (actionId: string): TreeNode[] => {
    const apiList = apisByAction.get(actionId) || [];
    return apiList.map((api) => ({
      id: api.id,
      label: "Api" as const,
      data: api,
    }));
  };

  const buildActionNodes = (stepId: string): TreeNode[] => {
    const actionList = actionsByStep.get(stepId) || [];
    return actionList.map((action) => ({
      id: action.id,
      label: "Action" as const,
      data: action,
      children: buildApiNodes(action.id),
    }));
  };

  const buildStepNodes = (scenarioId: string): TreeNode[] => {
    const stepList = stepsByScenario.get(scenarioId) || [];
    return stepList.map((step) => ({
      id: step.id,
      label: "Step" as const,
      data: step,
      children: buildActionNodes(step.id),
    }));
  };

  const buildScenarioNodes = (outcomeId: string): TreeNode[] => {
    const scenarioList = scenariosByOutcome.get(outcomeId) || [];
    return scenarioList.map((scenario) => ({
      id: scenario.id,
      label: "Scenario" as const,
      data: scenario,
      children: buildStepNodes(scenario.id),
    }));
  };

  const buildOutcomeNodes = (personaId: string): TreeNode[] => {
    const outcomeList = outcomesByPersona.get(personaId) || [];
    return outcomeList.map((outcome) => ({
      id: outcome.id,
      label: "Outcome" as const,
      data: outcome,
      children: buildScenarioNodes(outcome.id),
    }));
  };

  return personas.map((persona) => ({
    id: persona.id,
    label: "Persona" as const,
    data: persona,
    children: buildOutcomeNodes(persona.id),
  }));
};

// Main hook to fetch and build tree data
export const useFunctionalTreeData = (projectUuid: string, enabled: boolean = true) => {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["functional-tree", projectUuid],
    queryFn: async () => {
      const headers = getAuthHeaders();

      // Fetch all levels in parallel
      const results = await Promise.allSettled([
        fetchAllItems(projectUuid, "Persona", headers),
        fetchAllItems(projectUuid, "Outcome", headers),
        fetchAllItems(projectUuid, "Scenario", headers),
        fetchAllItems(projectUuid, "Step", headers),
        fetchAllItems(projectUuid, "Action", headers),
        fetchAllItems(projectUuid, "Api", headers),
      ]);

      // Extract data, using empty array for failed fetches
      const getData = (result: PromiseSettledResult<FunctionalGraphItem[]>) =>
        result.status === "fulfilled" ? result.value : [];

      const personas = getData(results[0]);
      const outcomes = getData(results[1]);
      const scenarios = getData(results[2]);
      const steps = getData(results[3]);
      const actions = getData(results[4]);
      const apis = getData(results[5]);

      // Check if any request failed
      const failures = results.filter(r => r.status === "rejected");
      if (failures.length > 0) {
        console.warn("Some tree data fetches failed:", failures);
      }

      // Build tree structure
      const tree = buildTree(personas, outcomes, scenarios, steps, actions, apis);

      return {
        tree,
        counts: {
          personas: personas.length,
          outcomes: outcomes.length,
          scenarios: scenarios.length,
          steps: steps.length,
          actions: actions.length,
          apis: apis.length,
        },
      };
    },
    enabled: enabled && !!projectUuid,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["functional-tree", projectUuid] });
    queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
  }, [queryClient, projectUuid]);

  return {
    treeData: query.data?.tree || [],
    counts: query.data?.counts || {
      personas: 0,
      outcomes: 0,
      scenarios: 0,
      steps: 0,
      actions: 0,
      apis: 0,
    },
    // isLoading is true only during initial load when there's no cached data
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPending: query.isPending,
    error: query.error,
    refresh,
    refetch: query.refetch,
  };
};
